import { useEffect, useRef, useState } from "react";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { IDropDownOption } from "@/types/DropDown";
import RestrictedPage from "@/consumer/restrictedPage";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import styles from "@/routes/admin/todoList.module.css";
import TodoCreateEditModal from "@/components/todos/todoCreateEditModal";
import GenericNotification from "@/components/notification/genericNotification";
import ImageViewer from "@/components/general/imageViewer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { Typography } from "@mui/material";
import { api } from "@/helper/api";
import { useNavigate, useParams } from "react-router-dom";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import { parseGbuDescription } from "@/components/todos/gbuDescriptionUtils";
import { replaceDate } from "@/helper/dateHandling";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { todoEntityAdapter } from "@/components/todos/todoEntityAdapter";

const GBU_TEMPLATE_NAME = "offener_punkt_aus_der_gefaehrdungsbeurteilung";

export default function TodoList() {
  /*eslint-disable */
  const [options, setOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const [subjectId, setSubjectId] = useState<string>("");
  const [urlSubjectName, setUrlSubjectName] = useState<string>("");
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [todoToEdit, setTodoToEdit] = useState<any>(null);
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentImageList, setCurrentImageList] = useState<any[]>([]);
  const todoRef = useRef(null);

  const { t } = useTranslation();
  usePageHeading(t("todo_multi"));
  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedChildSubjectId = useSelector(
    (state: RootState) => state.parent.subjectId
  );

  useEffect(() => {
    if (!urlSubjectId) return;
    setSubjectId(urlSubjectId);
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: urlSubjectId,
      successHandler: (res: any) => {
        setUrlSubjectName(res.data?.name || "");
      },
      failHandler: () => {
        setUrlSubjectName("");
      },
    });
  }, [urlSubjectId]);

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value
    ) {
      setSubjectId(selectedOption.value);
    }
  };

  const resetAsyncInput = () => {
    setSubjectId("");
  };

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    setUrlSubjectName("");
    setSubjectId("");
    navigate("/admin/todoList");
  };

  const handleCloseModal = () => {
    setOpenCreateModal(false);
    setTodoToEdit(null);
  };

  const handleSuccess = () => {
    setRefreshTable((prev) => !prev);
    setShowSuccessNotification(true);
  };

  const handleImageClick = (images: any[], imageIndex: number) => {
    setCurrentImageList(images);
    setSelectedImageIndex(imageIndex);
    setImageViewerOpen(true);
  };

  // currently disabled - enable again when tagging logic for todos is implemented

  // const additionalStaffColumn = () => {
  //   const column = [
  //     {
  //       header: t("kaer_staff_todo_question"),
  //       accessorKey: "is_staff",
  //       id: "staff",
  //       checkBox: true,
  //       enableForCreate: true,
  //       columnOnTop: true,
  //     },
  //   ];

  //   if (subdomain === "admin") {
  //     return column;
  //   }
  //   return;
  // };

  return (
    <RestrictedPage permissionRequired="frontend_view_todo_table">
      {urlSubjectId ? (
        <SelectedSubjectDisplay
          wrapperClassName={styles.dropDownSubjectSearch}
          subjectName={urlSubjectName}
          onReset={handleResetToSubjectSearch}
        />
      ) : (
        <div className={styles.dropDownSubjectSearch}>
          <FormFieldLabel label={t("subject")} htmlFor="todo-list-subject" />
          <DropDownSubjectSearch
            name="todo-list-subject"
            options={options}
            onChange={handleSubjectSelect}
            subdomain={subdomain}
            isClearable={true}
            resetInput={resetAsyncInput}
            customRef={todoRef}
            autofocus={true}
            useGlobalState={true}
          />
        </div>
      )}

      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        searchId={subjectId}
        entity={"todo"}
        entityAdapter={todoEntityAdapter}
        customCreatePresetValues={{ done: false }}
        customCreateHandler={() => {
          setShowSuccessNotification(false);
          setTodoToEdit(null);
          setOpenCreateModal(true);
        }}
        customEditHandler={(row: any) => {
          setShowSuccessNotification(false);
          setTodoToEdit(row.original);
          setOpenCreateModal(true);
        }}
        refreshTable={refreshTable}
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("subject"),
            accessorKey: "subject.name",
            id: "subject",
            enableForCreate: true,
            asyncEntity: "subject",
            asyncDropDown: true,
            customColumnFilterId: "subject_name",
            accessorFn: (row: any) => {
              if (subdomain === "admin") {
                return (
                  <NavigateToSubjectOverviewLink
                    subjectId={row.subject.id}
                    label={row.subject.name}
                  />
                );
              }
              return row.subject.name;
            },
          },
          {
            header: t("title"),
            accessorKey: "title",
            id: "title",
            customSortingKey: "title",
            sortDescFirst: false,
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
          },
          {
            header: t("type"),
            accessorKey: "type",
            required: true,
            filterVariant: "select",
            filterSelectOptions: [
              { value: "form", label: t("form") },
              { value: "upload", label: t("file_upload") },
              { value: "text", label: t("string") },
              { value: "gbu", label: t("gbu") },
            ],
            errorMessage: t("field_required"),
            id: "type",
            enableForCreate: true,
            selectOptions: [
              { value: "form", label: t("form") },
              { value: "upload", label: t("file_upload") },
              { value: "text", label: t("string") },
              { value: "gbu", label: t("gbu") },
            ],
          },
          {
            header: t("description"),
            accessorKey: "description",
            enableForCreate: true,
            id: "description",
            customSortingKey: "description",
            sortDescFirst: false,
            required: true,
            textarea: true,
            errorMessage: t("field_required"),
            Cell: ({ cell, row }: any) => {
              const rawDescription = cell.getValue();
              const type = row.original.type;
              const templateName =
                row.original?.template?.name ??
                row.original?.todoTemplate?.name ??
                row.original?.todo_template?.name;

              const shouldRenderGbuDescription =
                type === "gbu" || templateName === GBU_TEMPLATE_NAME;
              const gbu = shouldRenderGbuDescription
                ? parseGbuDescription(rawDescription)
                : null;

              if (shouldRenderGbuDescription && gbu) {
                return (
                  <div>
                    <div>
                      <span className={styles.boldText}>
                        {t("assessment_gbu_label")}:
                      </span>
                    </div>
                    <div className={styles.marginBottom}>
                      {gbu.assessment || ""}
                    </div>
                    <div>
                      <span className={styles.boldText}>
                        {t("dangerous_answer_gbu_label")}:
                      </span>
                    </div>
                    <div className={styles.marginBottom}>
                      {gbu.dangerous_answer || ""}
                    </div>
                    <div>
                      <span className={styles.boldText}>
                        {t("possible_remedy_gbu_label")}:
                      </span>
                    </div>
                    <div>{gbu.possible_remedy || ""}</div>
                  </div>
                );
              }
              return rawDescription || "";
            },
          },
          {
            header: t("buildings"),
            entity: "building",
            accessorKey: "building",
            id: "building",
            enableForCreate: true,
            customColumnFilterId: "building__name",
            customFilterParameter: "__icontains",
            accessorFn: (row: any) => {
              if (row.building !== null) {
                return row.building.name;
              } else return "";
            },
          },
          {
            header: t("note_attached_to_todo"),
            accessorKey: "note.payload",
            id: "todo_note",
            customColumnFilterId: "todo_note_payload",
            customFilterParameter: "__icontains",
            enableSorting: false,
            textarea: true,
            accessorFn: (row: any) => {
              return row.note?.payload;
            },
          },
          {
            header: t("attached_pictures"),
            accessorKey: "image_set",
            id: "todo_images",
            customColumnFilterId: "image_set_has_images",
            filterVariant: "checkbox",
            enableSorting: false,
            accessorFn: (row: any) => {
              const imageCount = row.image_set ? row.image_set.length : 0;
              if (imageCount === 0) {
                return null;
              }
              return (
                <Typography
                  component="span"
                  variant="body2"
                  color="secondary"
                  className={styles.viewImagesLink}
                  onClick={() => handleImageClick(row.image_set, 0)}
                >
                  {t("view_images")} ({imageCount})
                </Typography>
              );
            },
          },
          {
            header: t("target"),
            accessorKey: "target",
            customColumnFilterId: "target",
            customFilterParameter: "__icontains",
            id: "target",
            enableSorting: false,
            enableForCreate: true,
            accessorFn: (row: any) => {
              const targetData = [];
              if (row.target) {
                targetData.push(JSON.stringify(row.target));
              }

              return targetData.join(", ");
            },
            isJson: true,
          },
          {
            header: t("done"),
            accessorKey: "done",
            id: "done",
            enableForCreate: true,
            filterVariant: "select",
            filterSelectOptions: [
              { value: "true", label: t("yes") },
              { value: "false", label: t("no") },
            ],
            setNoFilterParameter: true,
            accessorFn: (row: any) => {
              if (row.done === true) return t("yes");
              else return t("no");
            },
            selectOptions: [
              { value: true, label: t("yes") },
              { value: false, label: t("no") },
            ],
          },
          {
            header: t("due_date"),
            accessorKey: "due_date",
            id: "due_date",
            filterVariant: "date",
            dateTimePicker: true,
            enableForCreate: true,
            required: true,
            errorMessage: t("field_required"),
            sortingFn: "datetime",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            sortingFn: "datetime",
            filterVariant: "date",
            id: "created_on",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",
            id: "created_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            sortingFn: "modified_on",
            filterVariant: "date",
            id: "modified_on",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",
            id: "modified_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
        ]}
        // additionalColumnsForCreate={additionalStaffColumn()}
      />
      <TodoCreateEditModal
        open={openCreateModal}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        mode={todoToEdit ? "edit" : "create"}
        todoToEdit={todoToEdit || undefined}
      />
      <GenericNotification
        message={
          showSuccessNotification ? t("todo_created_successfully") : null
        }
        variant="success"
        handleCloseSnackbar={() => setShowSuccessNotification(false)}
      />
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        images={currentImageList}
        initialImageIndex={selectedImageIndex}
        childrenSubjectId={String(selectedChildSubjectId)}
        doNotShowFileName={true}
      />
    </RestrictedPage>
  );
}
